import type { Image3D } from '~/domain/patient/image3d';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { Environment } from '~/engine/data/Environment';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useRegistryLifecycleVersion } from './useActiveEditTarget';

export interface ActiveFusionTarget {
    /** Series UID of the primary image; undefined when none is selected. */
    seriesUid: string | undefined;
    /**
     * The image fused onto the primary; undefined when nothing is fused.
     * Read from the primary row's resident Environment when there is one,
     * since it can refuse a fusion the entry recorded.
     */
    secondaryImage: Image3D | undefined;
    /**
     * The registration the pair is displayed through, in either direction;
     * undefined when nothing is fused or the fusion needs none (one frame of
     * reference). Read from the same place as `secondaryImage`.
     */
    displayedRegistration: RigidRegistration | undefined;
    /**
     * The registration a manual alignment on the pair edits: the displayed
     * one when a drag from the primary's side edits it, undefined when the
     * pair is displayed through the inverse of a registration, which such a
     * drag would edit backwards. Also undefined until the row's Environment
     * is resident: a pair is actionable only where its tiles can show the
     * effect.
     */
    registration: RigidRegistration | undefined;
    /** The pair's live Environment; undefined until the row's tiles hold it. */
    environment: Environment | undefined;
    /**
     * Whether the registration tools act on the pair: only an editable
     * registration on a resident row.
     */
    actionable: boolean;
}

/**
 * Resolves the fused pair the registration tools act on: the fusion of the
 * row whose primary image is the sidebar's. A registration tool arms only
 * when `registration` resolves; consumers fall back to pan otherwise.
 */
export function useActiveFusionTarget(): ActiveFusionTarget {
    const primaryImage = useSelectedPrimaryImage();
    useRegistryLifecycleVersion();

    if (!primaryImage) {
        return {
            seriesUid: undefined,
            secondaryImage: undefined,
            displayedRegistration: undefined,
            registration: undefined,
            environment: undefined,
            actionable: false,
        };
    }
    // The resident Environment can refuse a fusion the entry recorded, so
    // it is read first; the entry holds the fusion until the row is resident.
    const registry = getDatasetEnvironmentRegistry();
    const entry = registry.entryFor(primaryImage.seriesUid);
    const environment = registry.environmentFor(primaryImage.seriesUid);
    const secondaryImage = environment
        ? environment.secondaryImage?.parent
        : entry?.secondaryImage;
    const displayedRegistration = secondaryImage
        ? (environment ?? entry)?.registration
        : undefined;
    const registration =
        environment &&
        displayedRegistration?.editableFrom(primaryImage.frameOfReferenceUid)
            ? displayedRegistration
            : undefined;
    return {
        seriesUid: primaryImage.seriesUid,
        secondaryImage,
        displayedRegistration,
        registration,
        environment,
        actionable: registration !== undefined,
    };
}
